import { useEffect, useRef, useState } from 'react';

import { curvePoints, evalFalloff, FALLOFF_PRESETS, normalizeCurve, type Falloff } from '../../../state/sculpt.js';

const W = 200;
const H = 110;
const PAD = 8;
const HIT = 8;

/** Canvas pixel of curve point (distance x, strength y). */
function toPx([x, y]: readonly [number, number]): [number, number] {
  return [PAD + x * (W - 2 * PAD), H - PAD - y * (H - 2 * PAD)];
}

function fromPx(px: number, py: number): [number, number] {
  const clamp = (v: number) => Math.min(1, Math.max(0, v));
  return [clamp((px - PAD) / (W - 2 * PAD)), clamp((H - PAD - py) / (H - 2 * PAD))];
}

function cssVar(el: Element, name: string, fallback: string): string {
  return getComputedStyle(el).getPropertyValue(name).trim() || fallback;
}

/**
 * Brush falloff editor: presets, or drag the points of a custom curve
 * (x = distance from the brush center, y = strength). Click empty space to
 * add a point, double-click a point to remove it.
 */
export function FalloffCurve({ value, onChange }: { value: Falloff | null; onChange: (value: Falloff | null) => void }) {
  const canvas = useRef<HTMLCanvasElement | null>(null);
  const [drag, setDrag] = useState<number | null>(null);
  const points = curvePoints(value);

  useEffect(() => {
    const el = canvas.current;
    const ctx = el?.getContext('2d');
    if (!el || !ctx) return;
    const dpr = window.devicePixelRatio || 1;
    el.width = W * dpr;
    el.height = H * dpr;
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    ctx.clearRect(0, 0, W, H);
    const grid = cssVar(el, '--border', '#3a3d44');
    const line = cssVar(el, '--accent', '#e0a040');
    const text = cssVar(el, '--text-muted', '#8a8f98');
    ctx.strokeStyle = grid;
    ctx.lineWidth = 1;
    for (let i = 0; i <= 4; i++) {
      const x = PAD + (i / 4) * (W - 2 * PAD);
      const y = PAD + (i / 4) * (H - 2 * PAD);
      ctx.beginPath();
      ctx.moveTo(x, PAD);
      ctx.lineTo(x, H - PAD);
      ctx.moveTo(PAD, y);
      ctx.lineTo(W - PAD, y);
      ctx.stroke();
    }
    ctx.strokeStyle = line;
    ctx.lineWidth = 2;
    ctx.beginPath();
    for (let i = 0; i <= 64; i++) {
      const t = i / 64;
      // Show the value just inside the radius at the right edge.
      const [px, py] = toPx([t, evalFalloff(value, Math.min(t, 0.9999))]);
      if (i === 0) ctx.moveTo(px, py);
      else ctx.lineTo(px, py);
    }
    ctx.stroke();
    if (Array.isArray(value)) {
      ctx.fillStyle = line;
      for (const p of points) {
        const [px, py] = toPx(p);
        ctx.beginPath();
        ctx.arc(px, py, 3.5, 0, Math.PI * 2);
        ctx.fill();
      }
    }
    ctx.fillStyle = text;
    ctx.font = '9px sans-serif';
    ctx.fillText('center', PAD + 2, H - 1);
    ctx.fillText('edge', W - PAD - 20, H - 1);
  }, [value, points]);

  const at = (e: React.PointerEvent): [number, number] => {
    const r = e.currentTarget.getBoundingClientRect();
    return [((e.clientX - r.left) / r.width) * W, ((e.clientY - r.top) / r.height) * H];
  };
  const nearest = (px: number, py: number) => {
    let best = -1;
    let bestD = HIT;
    points.forEach((p, i) => {
      const [x, y] = toPx(p);
      const d = Math.hypot(x - px, y - py);
      if (d < bestD) {
        bestD = d;
        best = i;
      }
    });
    return best;
  };

  return (
    <div className="falloff-curve">
      <div className="falloff-presets" role="group" aria-label="Falloff presets">
        <button type="button" className={`tb-btn${value === null ? ' active' : ''}`} title="The brush's own profile" onClick={() => onChange(null)}>
          Default
        </button>
        {FALLOFF_PRESETS.map((p) => (
          <button key={p} type="button" className={`tb-btn${value === p ? ' active' : ''}`} onClick={() => onChange(p)}>
            {p[0]!.toUpperCase() + p.slice(1)}
          </button>
        ))}
      </div>
      <canvas
        ref={canvas}
        style={{ width: W, height: H }}
        title="Drag points to shape the falloff · click to add · double-click to remove"
        onPointerDown={(e) => {
          const [px, py] = at(e);
          let i = nearest(px, py);
          let pts = points;
          if (i < 0) {
            pts = normalizeCurve([...points, fromPx(px, py)]);
            const [x] = fromPx(px, py);
            i = pts.findIndex((p) => Math.abs(p[0] - x) < 1e-6);
          }
          onChange(pts);
          setDrag(i);
          e.currentTarget.setPointerCapture(e.pointerId);
        }}
        onPointerMove={(e) => {
          if (drag === null) return;
          const [px, py] = at(e);
          const next = points.map((p) => [...p] as [number, number]);
          const p = next[drag];
          if (!p) return;
          const [x, y] = fromPx(px, py);
          // Points keep their order; the end points stay at the center / edge.
          const lo = drag === 0 ? 0 : next[drag - 1]![0] + 0.01;
          const hi = drag === next.length - 1 ? 1 : next[drag + 1]![0] - 0.01;
          p[0] = drag === 0 ? 0 : drag === next.length - 1 ? 1 : Math.min(hi, Math.max(lo, x));
          p[1] = y;
          onChange(next.map(([a, b]) => [Math.round(a * 1000) / 1000, Math.round(b * 1000) / 1000]));
        }}
        onPointerUp={() => setDrag(null)}
        onDoubleClick={(e) => {
          const [px, py] = at(e as unknown as React.PointerEvent);
          const i = nearest(px, py);
          if (i > 0 && i < points.length - 1) onChange(points.filter((_, k) => k !== i));
        }}
      />
    </div>
  );
}
